import React, { useCallback, useEffect, useLayoutEffect, useReducer, useState } from "react";
import itemService from "../services/itemService";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ItemModel } from "../models/ItemModel";
import ItemImageUploadComp from "../components/ItemImageUploadComp";
import { useCategorySelector } from "../hooks/useItemCategorySelector";
import { useImageUpload } from "../hooks/useImageUpload";
import itemFormReducer, { ItemFormState, ItemValidatonErrors } from "../reducers/itemFormReducer";
import { formatPrice } from "../utils/currencyUtils";
import TruncateTextComp from "../components/TruncateTextComp";
import EditBulkDiscountComp from "../components/EditBulkDiscountComp";
import EditSellingConditionsComp from "../components/EditSellingConditionsComp";
import EditInfoLinkComp from "../components/EditInfoLinkComp";
import { ScrollableRefProps } from "../App";
import ConfirmDialogueComp from "../components/ConfirmDialogueComp";
import { useAppContext } from "../context/AppContext";
import EditItemReturnPolicyComp from "../components/EditItemReturnPolicyComp";
import EditItemShippingPolicyComp from "../components/EditItemShippingPolicyComp";

const isNullOrWhiteSpace = (value?: string | null) => {
    return !value || value.trim() === "";
};

const getPolicyDisplay = (
    useStorePolicy: boolean,
    useItemPolicy: boolean,
    itemPolicyText?: string,
    storePolicyText?: string
) => {
    const parts: string[] = [];

    if (useStorePolicy && !isNullOrWhiteSpace(storePolicyText)) {
        parts.push(storePolicyText!.trim());
    }

    if (useItemPolicy && !isNullOrWhiteSpace(itemPolicyText)) {
        parts.push(itemPolicyText!.trim());
    }

    if (parts.length === 0) {
        return "-";
    }

    return parts.join("\n\n");
};

const ViewItemPage = ({ scrollContainerRef }: ScrollableRefProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { seller, setSeller } = useAppContext();
    const { item: passedItem, categories } = location.state || {};
    const { itemId } = useParams<{ itemId: string }>();

    const initialState: ItemFormState = {
        item: new ItemModel(passedItem ?? {}),
        errors: {},
        isSubmitting: false,
        isDeleting: false,
        serviceSuccess: "",
        serviceError: "",
    };
    const [state, dispatch] = useReducer(itemFormReducer, initialState);
    const [bulkDiscountModalVisible, setBulkDiscountVisible] = useState(false);
    const [sellingConditionsVisible, setSellingConditionsVisible] = useState(false);
    const [infoLinkVisible, setInfoLinkVisible] = useState(false);
    const [confirmDeleteModalVisible, setConfirmDeleteModalVisible] = useState(false);
    const [itemReturnPolicyVisible, setItemReturnPolicyVisible] = useState(false);
    const [itemShippingPolicyVisible, setItemShippingPolicyVisible] = useState(false);

    // Category dropdown state and functions
    const { getParentCategoryNameById, getSubCategoryNameById } = useCategorySelector(categories);

    // Image upload state and functions
    const { images, setImages } = useImageUpload();



    const loadItem = useCallback(async (itemId: string) => {
        if (!itemId) return;
        if (!state.item?.itemId) {
            const fetchedItem = await itemService.getItem(parseInt(itemId));
            dispatch({ type: "SET_ITEM", payload: fetchedItem });
            setImages(fetchedItem.images);
        }
        else {
            setImages(state.item.images);
        }
    }, [setImages]);


    useLayoutEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [scrollContainerRef]);

    useEffect(() => {
        if (itemId) {
            loadItem(itemId);
        }
    }, [itemId, loadItem]);

    const validateItemFeatured = (featured: boolean) => {
        const errors: ItemValidatonErrors = {};

        if (featured && images.length < 3) {
            errors.featured = "Featured items must have at least 3 photos";
        }

        dispatch({ type: "SET_ERRORS", payload: errors });
        return Object.keys(errors).length === 0;
    }
    const handleListedChange = (checked: boolean) => {
        updateItemListed(checked);
    }

    const handleFeaturedChange = (checked: boolean) => {
        const isValid = validateItemFeatured(checked);
        if (isValid) {
            updateItemFeatured(checked);
        }
    };

    const updateItemFeatured = async (checked: boolean) => {
        dispatch({ type: "SET_ERROR", payload: "" });
        dispatch({ type: "SET_SUCCESS", payload: "" });
        try {
            const updateResult = await itemService.updateItemFeatured(state.item.itemId, checked);
            if (updateResult === "Success") {
                dispatch({ type: "SET_ITEM", payload: { featured: checked } });
                dispatch({ type: "SET_SUCCESS", payload: "Item featured status updated successfully." });
            }
            else if (updateResult === "Limit") {
                throw new Error("You can only have 3 featured items at a time. Please unfeature one before featuring another.");
            }
            else {
                throw new Error("Failed to update item featured status.");
            }
        }
        catch (error) {
            const errorMessage = (error instanceof Error) ? error.message : "Failed to update item featured status.";
            dispatch({ type: "SET_ERROR", payload: errorMessage });
        }
    }

    const updateItemListed = async (checked: boolean) => {
        dispatch({ type: "SET_ERROR", payload: "" });
        dispatch({ type: "SET_SUCCESS", payload: "" });
        try {
            const updateResult = await itemService.updateItemListed(state.item.itemId, checked);
            if (updateResult === "Success") {
                dispatch({ type: "SET_ITEM", payload: { listed: checked } });
                dispatch({ type: "SET_SUCCESS", payload: "Item listed status updated successfully." });
            }
            else {
                throw new Error("Failed to update item listed status.");
            }
        } catch (error) {
            const errorMessage = (error instanceof Error) ? error.message : "Failed to update item listed status.";
            dispatch({ type: "SET_ERROR", payload: errorMessage });
        }
    }

    const handleDeleteItem = () => {
        if (itemId) {
            setConfirmDeleteModalVisible(true);
        }
    }

    const handleDelete = () => {
        dispatch({ type: "SET_DELETING", payload: true });
        const deleteItem = async () => {
            try {
                if (itemId) {
                    const deletedSucceed = await itemService.deleteItem(parseInt(itemId));
                    if (deletedSucceed === "Success") {
                        dispatch({ type: "SET_SUCCESS", payload: "Item deleted successfully" });
                        navigate("/items"); // Redirect to items page after deletion
                    }
                    else {
                        throw new Error("Failed to delete item. Please try again.");
                    }
                }

            }
            catch (error: any) {
                dispatch({ type: "SET_ERROR", payload: error.message || "Unknown error occurred" });
            }
            finally {
                dispatch({ type: "SET_DELETING", payload: false });
            }
        }

        deleteItem();
        setConfirmDeleteModalVisible(false); // Hide the modal after confirming delete

    };

    const handleCancel = () => {
        setConfirmDeleteModalVisible(false); // Hide the modal on cancel
    };

    const returnPolicyText = getPolicyDisplay(
        state.item.useStoreReturnPolicy,
        state.item.useItemReturnPolicy,
        state.item.itemReturnPolicy,
        seller?.returnPolicy
    );

    const shippingPolicyText = getPolicyDisplay(
        state.item.useStoreShippingPolicy,
        state.item.useItemShippingPolicy,
        state.item.itemShippingPolicy,
        seller?.shippingPolicy
    );

    return (
        <div className="mt-3 mb-5">
            <div className="card rounded-0">
                <div className="card-body  p-4">
                    <h5 className="card-title mb-4">Item #{state.item.itemId}</h5>
                    <h5 className=" mb-2">{state.item.itemName}</h5>
                    <ItemImageUploadComp
                        item={state.item}
                        itemImages={images}
                        viewOnly={true}
                    ></ItemImageUploadComp>

                    <div className="row g-3 mt-3" >

                        <div className="col-md-12 mb-3">
                            <label className="text-label">Description</label>
                            <span className="text-value ">
                                <TruncateTextComp text={state.item.description}></TruncateTextComp>
                            </span>
                        </div>
                        <div className="col-md-6">
                            <label className="text-label">Category</label>
                            <p className="text-value mb-2">{getParentCategoryNameById(state.item.categoryId)}</p>
                        </div>

                        <div className="col-md-6">
                            <label className="text-label">Subcategory</label>
                            <p className="text-value mb-2">{getSubCategoryNameById(state.item.categoryId) || '-'}</p>
                        </div>
                        <div className="col-md-12">
                            <label className="text-label">Keywords</label>
                            <p className="text-value mb-2">{state.item.keywords || '-'}</p>
                        </div>
                        <div className="col-md-4">
                            <label className="text-label">Price</label>
                            <p className="text-value mb-1">{formatPrice(state.item.price ?? 0)}</p>
                            <small className="text-muted">(Shipping cost is not included in the price) </small>
                        </div>
                        <div className='col-md-12 mt-3 d-flex'>
                            <Link to={`/items/edit/${state.item.itemId}`}
                                state={{ item: state.item, categories }} className="btn btn-outline-dark rounded-0 mt-3">Edit</Link>

                        </div>
                    </div>


                </div>
            </div>
            <div className="card rounded-0 mt-3">
                <div className="card-body  p-4">
                    <h5 className="card-title mb-4">Bulk Discount</h5>
                    <div className="row g-3  mb-3">
                        <div className="col-4">
                            <label className="text-label ">Bulk Discount</label>
                            <p className="text-value mb-2">{state.item.bulkDiscountEnabled ? 'Enabled' : 'Disabled'}</p>
                        </div>
                        <div className="col-4">
                            <label className="text-label">Minimum Quantity</label>
                            <p className="text-value mb-2">{state.item.bulkMinQty}</p>
                        </div>
                        <div className="col-4">
                            <label className="text-label">Bulk Discount</label>
                            <p className="text-value mb-2">{state.item.bulkDiscount}%</p>
                        </div>
                    </div>
                    <button type="button" className="btn btn-outline-dark rounded-0 mt-3 " onClick={() => setBulkDiscountVisible(true)} >Edit</button>
                </div>
                <EditBulkDiscountComp show={bulkDiscountModalVisible}
                    item={state.item}
                    onClose={() => setBulkDiscountVisible(false)}
                    onBulkDiscountUpdated={(updatedItem: ItemModel) => {
                        dispatch({
                            type: "SET_ITEM",
                            payload: {
                                bulkMinQty: updatedItem.bulkMinQty,
                                bulkDiscountEnabled: updatedItem.bulkDiscountEnabled,
                                bulkDiscount: updatedItem.bulkDiscount
                            }
                        });
                        setBulkDiscountVisible(false);
                    }} />
            </div>
            <div className="card rounded-0 mt-3">
                <div className="card-body  p-4">
                    <h5 className="card-title mb-4">Selling Conditions</h5>
                    <div className="row g-3 mb-3" >
                        <div className="col-md-4">
                            <label className="text-label mt-1">Minimum Offer Quantity</label>
                            <p className="text-value mb-2">{state.item.minOfferQty}</p>
                        </div>
                        <div className="col-md-8">
                            <label className="text-label mt-1">Available Regions</label>
                            <p className="text-value mb-2 one-line-text-ellipsis">{state.item.availableRegion || "-"}</p>
                        </div>
                    </div>
                    <button type="button" className="btn btn-outline-dark rounded-0 mt-3 " onClick={() => setSellingConditionsVisible(true)} >Edit</button>
                </div>

                <EditSellingConditionsComp show={sellingConditionsVisible}
                    item={state.item}
                    onClose={() => setSellingConditionsVisible(false)}
                    onSellingConditionsUpdated={(updatedItem: ItemModel) => {
                        dispatch({
                            type: "SET_ITEM",
                            payload: {
                                minOfferQty: updatedItem.minOfferQty,
                                availableRegion: updatedItem.availableRegion
                            }
                        });
                        setSellingConditionsVisible(false);
                    }} />
            </div>
            <div className="card rounded-0 mt-3">
                <div className="card-body  p-4">
                    <h5 className="card-title mb-4">Info & Support Link</h5>
                    <div className="row g-3 mb-3">
                        <div className="col-md-4">
                            <label className="text-label mt-1">Link Title</label>
                            <p className="text-value mb-2">{state.item.infoLinkTitle || "-"}</p>
                        </div>
                        <div className="col-md-4">
                            <label className="text-label mt-1">Link URL</label>
                            <p className="text-value mb-2">
                                {state.item.infoLinkUrl ? (
                                    <a
                                        href={state.item.infoLinkUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-500 underline"
                                    >
                                        {state.item.infoLinkUrl}
                                    </a>
                                ) : (
                                    <span>-</span>
                                )}
                            </p>
                        </div>
                        <div className="col-md-4">
                            <label className="text-label mt-1">Link Text</label>
                            <p className="text-value mb-2 one-line-text-ellipsis">{state.item.infoLinkText || "-"}</p>
                        </div>
                    </div>
                    <button type="button" className="btn btn-outline-dark rounded-0 mt-3 " onClick={() => setInfoLinkVisible(true)} >Edit</button>
                </div>
                <EditInfoLinkComp show={infoLinkVisible}
                    item={state.item}
                    onClose={() => setInfoLinkVisible(false)}
                    onInfoLinkUpdated={(updatedItem: ItemModel) => {
                        dispatch({
                            type: "SET_ITEM",
                            payload: {
                                infoLinkTitle: updatedItem.infoLinkTitle,
                                infoLinkUrl: updatedItem.infoLinkUrl,
                                infoLinkText: updatedItem.infoLinkText
                            }
                        });
                        setInfoLinkVisible(false);
                    }} />
            </div>
            <div className="card rounded-0 mt-3">
                <div className="card-body  p-4">
                    <h5 className="card-title mb-4">Shipping Policy</h5>
                    <div className="row g-3 mb-3">
                        <div className="col-md-12">
                            {/* <label className="text-label mt-1">Shipping Policy</label> */}
                            <span className="text-value">
                                <TruncateTextComp text={shippingPolicyText}></TruncateTextComp>
                            </span>
                        </div>
                    </div>
                    <button type="button" className="btn btn-outline-dark rounded-0 mt-3 " onClick={() => setItemShippingPolicyVisible(true)} >Edit</button>
                </div>
                <EditItemShippingPolicyComp 
                    show={itemShippingPolicyVisible}
                    item={state.item}
                    onClose={() => setItemShippingPolicyVisible(false)}
                    onItemShippingPolicyUpdated={(updatedItem: ItemModel) => {
                        dispatch({
                            type: "SET_ITEM",
                            payload: {
                                useStoreShippingPolicy: updatedItem.useStoreShippingPolicy,
                                useItemShippingPolicy: updatedItem.useItemShippingPolicy,
                                itemShippingPolicy: updatedItem.itemShippingPolicy
                            }
                        });
                        setItemShippingPolicyVisible(false);
                    }}
                />
            </div>
            <div className="card rounded-0 mt-3">
                <div className="card-body  p-4">
                    <h5 className="card-title mb-4">Return Policy</h5>
                    <div className="row g-3 mb-3">
                        <div className="col-md-12">
                            {/* <label className="text-label mt-1">Return Policy</label> */}
                            <span className="text-value">
                                <TruncateTextComp text={returnPolicyText}></TruncateTextComp>
                            </span>
                        </div>
                    </div>
                    <button type="button" className="btn btn-outline-dark rounded-0 mt-3 " onClick={() => setItemReturnPolicyVisible(true)} >Edit</button>
                </div>
                <EditItemReturnPolicyComp 
                    show={itemReturnPolicyVisible}
                    item={state.item}
                    onClose={() => setItemReturnPolicyVisible(false)}
                    onItemReturnPolicyUpdated={(updatedItem: ItemModel) => {
                        dispatch({
                            type: "SET_ITEM",
                            payload: {
                                useStoreReturnPolicy: updatedItem.useStoreReturnPolicy,
                                useItemReturnPolicy: updatedItem.useItemReturnPolicy,
                                itemReturnPolicy: updatedItem.itemReturnPolicy
                            }
                        });
                        setItemReturnPolicyVisible(false);
                    }}
                />
            </div>
            <div className="card rounded-0 mt-3">
                <div className="card-body  p-4">
                    <h5 className="card-title mb-4">Visibility</h5>
                    <div className="row g-3 mb-3">
                        <div className="col-md-6">
                            <label className="form-label">Featured</label>
                            <div className="form-check form-switch ms-4 ps-5 mb-3">
                                <input
                                    className={`form-check-input fs-3 ${state.errors.featured ? 'is-invalid' : ''}`}
                                    type="checkbox"
                                    id="featuredSwitch"
                                    checked={state.item.featured}

                                    onChange={(e) => handleFeaturedChange(e.target.checked)}
                                />

                            </div>
                            {state.errors.featured && (<div className="invalid-feedback" style={{ display: 'block' }}>{state.errors.featured}</div>)}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Listed</label>
                            <div className="form-check form-switch  ms-4 ps-5 mb-3">
                                <input
                                    className={`form-check-input fs-3`}
                                    type="checkbox"
                                    id="listedSwitch"
                                    checked={state.item.listed}
                                    onChange={(e) => handleListedChange(e.target.checked)}
                                />

                            </div>
                        </div>
                    </div>
                    {state.serviceSuccess && (
                        <div className="col-md-12 clearfix mt-2">
                            <div className="alert alert-success p-2 mb-0">{state.serviceSuccess}</div>
                        </div>
                    )}
                    {state.serviceError && (
                        <div className="col-md-12 clearfix mt-2">
                            <div className="alert alert-danger p-2 mb-0">{state.serviceError}</div>
                        </div>
                    )}
                </div>
            </div>
            <div className="col-md-12 my-3">
                <Link to="/items" className="btn btn-outline-dark rounded-0 ">Back</Link>
                <button type="button" className="btn btn-danger rounded-0 float-end" onClick={handleDeleteItem} disabled={state.isDeleting}>
                    {state.isDeleting ? (<><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Deleting ...</>) : ("Delete")}
                </button>
            </div>
            <ConfirmDialogueComp
                show={confirmDeleteModalVisible}
                title="Confirm"
                message="Are you sure you want to delete this item?"
                onConfirm={handleDelete}
                onCancel={handleCancel}
            />
        </div>




    );
}

export default ViewItemPage;